// - Comprehensive pre-game intelligence report modal for Patriots vs Jets
// - Displays full report structure with validated post-game results
import { CheckCircle2, TrendingUp, Users, Target, Cloud, Newspaper, BarChart3, Map } from 'lucide-react'

import { useState } from 'react'

import { Dialog, DialogContent } from './ui/dialog'
import { Badge } from './ui/badge'
import { ScrollArea } from './ui/scroll-area'

export function ExampleReport({ open, onOpenChange }) {
  const [viewMode, setViewMode] = useState('')

  const viewOptions = [
    {
      id: 'simple',
      title: 'Simple View',
      description: 'High-level predictions, outcomes, and accuracy receipts.',
      icon: '✓'
    },
    {
      id: 'detailed',
      title: 'Detailed View',
      description: 'Full seven-layer intelligence report with tactical context.',
      icon: '∞'
    }
  ]

  const simpleStats = [
    {
      label: 'Predicted Winner',
      predicted: 'Patriots (High Confidence)',
      actual: 'Patriots 27-14'
    },
    {
      label: 'Projected Margin',
      predicted: '10-14 points',
      actual: '13-point margin'
    },
    {
      label: 'Expected Total',
      predicted: '38-42 points',
      actual: '41 points'
    }
  ]

  const simpleReceipts = [
    {
      title: 'TreVeyon Henderson Multi-TD',
      summary: 'Projected 18-22 touches with multi-score upside.',
      outcome: 'Delivered 3 total TDs on 24 touches.'
    },
    {
      title: 'Justin Fields Under 150 Passing Yards',
      summary: 'Forecasted 90-140 passing yards amid depleted WR room.',
      outcome: 'Held to 116 yards with conservative game plan.'
    },
    {
      title: 'Scenario Accuracy',
      summary: 'Most-likely “Methodical Mauling” (62% confidence).',
      outcome: 'Game followed projected script with run-heavy control.'
    }
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black border border-white/10 text-white !max-w-[95vw] w-full sm:max-w-[90vw] lg:max-w-[1400px] !h-[95vh] sm:!h-[90vh] p-0 overflow-hidden flex flex-col">
        <ScrollArea className="flex-1 min-h-0">
          <div className="p-4 sm:p-6 md:p-8 lg:p-12">
            {/* Header */}
            <div className="mb-8 sm:mb-12 pb-6 sm:pb-8 border-b border-white/10">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
                <Badge className="bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/30 text-xs uppercase tracking-wider">
                  NFL Week 11 • TNF
                </Badge>
                <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 text-xs">
                  Pre-Game Intelligence
                </Badge>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-3 sm:mb-4 tracking-tight">
                Patriots vs Jets
              </h1>
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-4 text-gray-400 text-xs sm:text-sm">
                <span>November 13, 2025 • 8:15 PM ET</span>
                <span className="hidden sm:inline text-white/20">•</span>
                <span>Gillette Stadium, Foxborough, MA</span>
                <span className="hidden sm:inline text-white/20">•</span>
                <span>Patriots (8-2) vs Jets (2-7)</span>
              </div>
            </div>

            {/* View Selection */}
            <div className="mb-8 sm:mb-12">
              <p className="text-xs sm:text-sm text-gray-500 uppercase tracking-[0.2em] mb-4 sm:mb-6">
                Choose Your View
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                {viewOptions.map((option) => {
                  const isActive = viewMode === option.id
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setViewMode(option.id)}
                      className={`text-left rounded-xl sm:rounded-2xl border p-4 sm:p-6 transition-all ${
                        isActive
                          ? 'border-[#00E5FF]/60 bg-[#00E5FF]/10 shadow-[0_0_30px_0_rgba(0,229,255,0.2)]'
                          : 'border-white/10 bg-black/40 hover:border-white/30'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/5 border border-white/10 text-sm font-heading text-white">
                          {option.icon}
                        </span>
                        <div>
                          <p className="font-heading text-lg font-semibold text-white">{option.title}</p>
                          <p className="text-xs text-gray-400">{option.description}</p>
                        </div>
                      </div>
                      <p className="text-[11px] leading-relaxed text-gray-500">
                        {option.id === 'simple'
                          ? 'Best for quick validation of accuracy before diving deeper.'
                          : 'Includes executive summary, tactical breakdowns, and receipts.'}
                      </p>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Prompt if no selection */}
            {!viewMode && (
              <div className="rounded-xl border border-white/10 bg-black/50 p-6 text-center text-sm text-gray-400">
                Select a view above to explore the Patriots vs Jets intelligence drop.
              </div>
            )}

            {viewMode === 'simple' && (
              <section className="mb-10 sm:mb-12 md:mb-16">
                <div className="mb-6">
                  <h2 className="text-lg sm:text-xl md:text-2xl font-heading font-bold uppercase tracking-wider mb-2">
                    Snapshot
                  </h2>
                  <p className="text-sm text-gray-400">
                    Quick read of what PitchMetric projected versus what happened on Thursday night.
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  {simpleStats.map((stat) => (
                    <article key={stat.label} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">{stat.label}</p>
                      <div className="space-y-2">
                        <div>
                          <p className="text-[10px] text-gray-500">Predicted</p>
                          <p className="text-sm font-heading text-white">{stat.predicted}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-500">Actual</p>
                          <p className="text-sm font-heading text-emerald-400">{stat.actual}</p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                <div className="mt-8">
                  <h3 className="text-base sm:text-lg font-heading font-semibold mb-4">Receipts</h3>
                  <div className="grid gap-4 sm:grid-cols-3">
                    {simpleReceipts.map((receipt) => (
                      <article key={receipt.title} className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                        <p className="text-xs font-heading text-white mb-1">{receipt.title}</p>
                        <p className="text-[11px] text-gray-400">{receipt.summary}</p>
                        <div className="mt-3 rounded-lg bg-black/40 p-3 border border-emerald-500/20">
                          <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 mb-1">Outcome</p>
                          <p className="text-xs text-white">{receipt.outcome}</p>
                        </div>
                      </article>
                    ))}
                  </div>

                  <div className="mt-8 grid gap-4 sm:grid-cols-2">
                    <article className="rounded-xl border border-white/10 bg-black/40 p-4">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Core Thesis</p>
                      <p className="text-sm text-gray-300 leading-relaxed">
                        Patriots control tempo, limit turnovers, and grind out a double-digit win against a depleted Jets roster.
                      </p>
                    </article>
                    <article className="rounded-xl border border-white/10 bg-black/40 p-4">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Scenario Hit</p>
                      <p className="text-sm text-gray-300 leading-relaxed">
                        Actual game mirrored the 62% “Methodical Mauling” scenario: run-heavy drives, cold-weather control, and late-game kill shots.
                      </p>
                    </article>
                  </div>
                </div>
              </section>
            )}

            {viewMode === 'detailed' && (
<>
            {/* Executive Summary */}
            <section className="mb-10 sm:mb-12 md:mb-16">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-[#00E5FF]" />
                <h2 className="text-lg sm:text-xl md:text-2xl font-heading font-bold uppercase tracking-wider">Executive Summary</h2>
              </div>

              <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="lg:col-span-2 rounded-xl sm:rounded-2xl border border-white/10 bg-gradient-to-br from-[#00E5FF]/5 to-transparent p-4 sm:p-6 md:p-8">
                  <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-2 sm:mb-3">Core Thesis</h3>
                  <p className="text-lg sm:text-xl font-heading font-semibold text-white mb-3 sm:mb-4 leading-relaxed">
                    Patriots by 10-14 points in a defensively-controlled, methodical victory
                  </p>
                  <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                    New England's superior quarterback play, coaching discipline, and home-field advantage will overwhelm a depleted Jets roster playing on short rest in inhospitable weather conditions. The Patriots' ability to sustain drives and convert on fourth down will contrast sharply with the Jets' anemic passing attack.
                  </p>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-black/40 p-4 sm:p-6">
                    <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Predicted Winner</h3>
                    <p className="text-xl sm:text-2xl font-heading font-bold text-[#00E5FF]">Patriots</p>
                    <Badge className="mt-2 bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-xs">
                      High Confidence
                    </Badge>
                  </div>
                  <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-black/40 p-4 sm:p-6">
                    <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Expected Total</h3>
                    <p className="text-xl sm:text-2xl font-heading font-bold text-white">38-42 points</p>
                    <p className="text-xs sm:text-sm text-gray-400 mt-2">Low-to-medium scoring</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {[
                  { label: 'Most Likely Scenario', value: '62%', desc: 'Methodical Mauling' },
                  { label: 'Projected Score', value: '27-14', desc: 'Patriots control' },
                  { label: 'Game Style', value: 'Run-Heavy', desc: 'Ball control offense' },
                  { label: 'Upset Probability', value: '3%', desc: 'Jets lack firepower' }
                ].map((stat) => (
                  <div key={stat.label} className="rounded-lg sm:rounded-xl border border-white/5 bg-white/[0.02] p-3 sm:p-4 md:p-5">
                    <p className="text-[10px] sm:text-xs uppercase tracking-wider text-gray-500 mb-1">{stat.label}</p>
                    <p className="text-lg sm:text-xl md:text-2xl font-heading font-bold text-white mb-1">{stat.value}</p>
                    <p className="text-[10px] sm:text-xs text-gray-400 line-clamp-2">{stat.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Key Drivers */}
            <section className="mb-10 sm:mb-12 md:mb-16">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <Target className="h-4 w-4 sm:h-5 sm:w-5 text-[#00E5FF]" />
                <h2 className="text-lg sm:text-xl md:text-2xl font-heading font-bold uppercase tracking-wider">Key Drivers</h2>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {[
                  {
                    title: 'Quarterback Disparity Gap',
                    weight: '35%',
                    desc: "Drake Maye's MVP-caliber play (71.8% completion, 20 TDs, 5 INTs) versus Justin Fields' struggles (63.5% completion, 169.6 pass yards/game — 32nd in NFL) creates an insurmountable mismatch.",
                    stats: ['Maye: 109.3 passer rating', 'Fields: 4 games under 60 yards', 'Processing speed advantage']
                  },
                  {
                    title: 'Jets Roster Decimation',
                    weight: '25%',
                    desc: 'Loss of Garrett Wilson (IR, knee) hours before kickoff, combined with Williams/Gardner trades, left Jets without top offensive weapon and defensive anchors.',
                    stats: ['Wilson: 36 rec, 395 yds, 4 TDs', 'Williams traded to Dallas', 'Gardner traded to Indy']
                  },
                  {
                    title: 'Weather & Home Advantage',
                    weight: '15%',
                    desc: 'Weather conditions (39°F at kickoff, 8 MPH winds with 15-17 MPH gusts) favor ball security and run-heavy schemes. Patriots 85.7% fourth-down conversion at home.',
                    stats: ['Cold, windy conditions', 'Slippery ball handling', 'Crowd noise advantage']
                  }
                ].map((driver) => (
                  <div key={driver.title} className="rounded-xl sm:rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent p-4 sm:p-5 md:p-6 hover:border-[#00E5FF]/30 transition-colors">
                    <div className="flex items-center justify-between mb-2 sm:mb-3">
                      <Badge variant="outline" className="border-white/20 text-gray-300 text-xs">
                        Weight: {driver.weight}
                      </Badge>
                    </div>
                    <h3 className="text-base sm:text-lg md:text-xl font-heading font-bold text-white mb-2 sm:mb-3">
                      {driver.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-400 leading-relaxed mb-3 sm:mb-4">
                      {driver.desc}
                    </p>
                    <div className="space-y-1 sm:space-y-1.5">
                      {driver.stats.map((stat) => (
                        <div key={stat} className="flex items-center gap-2 text-[10px] sm:text-xs text-gray-500">
                          <div className="h-1 w-1 rounded-full bg-[#00E5FF] flex-shrink-0" />
                          <span>{stat}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Team & Player Intelligence */}
            <section className="mb-10 sm:mb-12 md:mb-16">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-[#00E5FF]" />
                <h2 className="text-lg sm:text-xl md:text-2xl font-heading font-bold uppercase tracking-wider">Team & Player Intelligence</h2>
              </div>

              <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                {[
                  {
                    player: 'TreVeyon Henderson',
                    team: 'Patriots RB',
                    confidence: '92%',
                    projection: '18-22 touches, 85-105 total yards, 2-3 TDs',
                    rationale: 'With Rhamondre Stevenson out (toe injury, 3rd consecutive game), Henderson elevated to RB1 role. Averaged 15.7 carries in Weeks 9-10 with 4 total TDs. Jets goal-line defense ranked 30th in NFL (71% TD rate allowed).',
                    splits: ['147 yards, 2 TDs vs Tampa (Week 10)', '10.5 YPC on 14 carries', 'Explosive burst on FieldTurf']
                  },
                  {
                    player: 'Justin Fields',
                    team: 'Jets QB',
                    confidence: '85%',
                    projection: 'Under 150 passing yards, 58-65% completion',
                    rationale: "Without Wilson, Fields loses primary target. Patriots' defense ranked 6th in PPG allowed (19.2). Fields has thrown under 60 yards in 4 games this season. Holds ball 3.37 seconds (longest in NFL), exposing him to Patriots' pass rush.",
                    splits: ['54 yards vs Cleveland (Week 10)', '116 yards actual vs Patriots', '12.3% sack rate (2nd-worst)']
                  },
                  {
                    player: 'Stefon Diggs',
                    team: 'Patriots WR',
                    confidence: '78%',
                    projection: '7-10 catches, 95-115 yards, 0-1 TD',
                    rationale: 'Without Sauce Gardner (traded to Indy), Jets secondary vulnerable to intermediate routes. Diggs averaging 63 yards/game at home with three 100-yard performances this season. Maye-Diggs timing creates consistent target volume.',
                    splits: ['50 rec, 554 yards through Week 10', '5-46-1 vs Tampa Bay', 'Route precision advantage']
                  },
                  {
                    player: 'Drake Maye',
                    team: 'Patriots QB',
                    confidence: '82%',
                    projection: '270-300 yards, 2-3 total TDs, 0-1 INT',
                    rationale: "MVP narrative building (+225 odds, 2nd behind Stafford). Maye's 71.8% completion rate and 20:5 TD:INT ratio through Week 10 showcase elite efficiency. Jets defense ranked 26th in PPG allowed (26.8). National TNF audience amplifies performance stakes.",
                    splits: ['7-game win streak entering', '122.7 deep-ball passer rating', 'Elite pocket mobility']
                  }
                ].map((intel) => (
                  <div key={intel.player} className="rounded-xl sm:rounded-2xl border border-white/10 bg-black/40 p-4 sm:p-5 md:p-6">
                    <div className="flex items-start justify-between mb-3 sm:mb-4 gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg md:text-xl font-heading font-bold text-white mb-1">
                          {intel.player}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500">{intel.team}</p>
                      </div>
                      <Badge className="bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/30 text-xs flex-shrink-0">
                        {intel.confidence}
                      </Badge>
                    </div>
                    <div className="mb-3 sm:mb-4 p-3 sm:p-4 rounded-lg bg-white/[0.02] border border-white/5">
                      <p className="text-[10px] sm:text-xs uppercase tracking-wider text-gray-500 mb-1">Projection</p>
                      <p className="text-xs sm:text-sm font-medium text-white">{intel.projection}</p>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-400 leading-relaxed mb-3 sm:mb-4">
                      {intel.rationale}
                    </p>
                    <div className="space-y-1 sm:space-y-1.5">
                      {intel.splits.map((split) => (
                        <div key={split} className="flex items-start gap-2 text-[10px] sm:text-xs text-gray-500">
                          <div className="h-1 w-1 rounded-full bg-gray-600 flex-shrink-0 mt-1.5" />
                          <span className="break-words">{split}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Injury Impact */}
              <div className="rounded-xl sm:rounded-2xl border border-orange-500/20 bg-orange-500/5 p-4 sm:p-5 md:p-6">
                <h3 className="text-base sm:text-lg font-heading font-bold text-orange-300 mb-3 sm:mb-4">Injury & Availability Impact</h3>
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Patriots</p>
                    <div className="space-y-2 text-sm text-gray-400">
                      <p><span className="text-red-400 font-medium">OUT:</span> Rhamondre Stevenson (RB, toe) — 3rd game</p>
                      <p><span className="text-red-400 font-medium">OUT:</span> Austin Hooper (TE, concussion)</p>
                      <p className="text-xs text-gray-500 mt-3">Henderson elevated to RB1, Hunter Henry primary red-zone target</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Jets</p>
                    <div className="space-y-2 text-sm text-gray-400">
                      <p><span className="text-red-400 font-medium">IR:</span> Garrett Wilson (WR, knee) — Ruled out Thursday morning</p>
                      <p><span className="text-yellow-400 font-medium">TRADED:</span> Quinnen Williams (DT) — To Dallas</p>
                      <p><span className="text-yellow-400 font-medium">TRADED:</span> Sauce Gardner (CB) — To Indianapolis</p>
                      <p className="text-xs text-gray-500 mt-3">Catastrophic talent depletion: -2.3 standard deviation drop</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Environmental Factors */}
            <section className="mb-10 sm:mb-12 md:mb-16">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <Cloud className="h-4 w-4 sm:h-5 sm:w-5 text-[#00E5FF]" />
                <h2 className="text-lg sm:text-xl md:text-2xl font-heading font-bold uppercase tracking-wider">Environmental Factors</h2>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-gradient-to-br from-blue-500/5 to-transparent p-4 sm:p-5 md:p-6">
                  <h3 className="text-base sm:text-lg font-heading font-bold text-white mb-3 sm:mb-4">Weather Impact</h3>
                  <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                    <div>
                      <p className="text-gray-500 text-xs mb-1">Temperature</p>
                      <p className="text-white font-medium">39°F kickoff → 31°F by Q4</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs mb-1">Wind</p>
                      <p className="text-white font-medium">8 MPH, gusts to 17 MPH</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs mb-1">Conditions</p>
                      <p className="text-white font-medium">Partly cloudy, 79% humidity</p>
                    </div>
                    <div className="pt-3 border-t border-white/5">
                      <p className="text-gray-400 text-xs leading-relaxed">
                        Cold hands and wind gusts reduce deep-throw accuracy by 12-18%. Favors ball-control offenses and run-heavy schemes.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-gradient-to-br from-green-500/5 to-transparent p-4 sm:p-5 md:p-6">
                  <h3 className="text-base sm:text-lg font-heading font-bold text-white mb-3 sm:mb-4">Venue & Surface</h3>
                  <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                    <div>
                      <p className="text-gray-500 text-xs mb-1">Stadium</p>
                      <p className="text-white font-medium">Gillette Stadium</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs mb-1">Surface</p>
                      <p className="text-white font-medium">FieldTurf (artificial)</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs mb-1">Attendance</p>
                      <p className="text-white font-medium">62,417 (announced)</p>
                    </div>
                    <div className="pt-3 border-t border-white/5">
                      <p className="text-gray-400 text-xs leading-relaxed">
                        Crowd noise forces Jets into 3 false starts and 2 delay-of-game penalties. FieldTurf favors speed backs like Henderson.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-gradient-to-br from-purple-500/5 to-transparent p-4 sm:p-5 md:p-6">
                  <h3 className="text-base sm:text-lg font-heading font-bold text-white mb-3 sm:mb-4">Short Rest Impact</h3>
                  <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                    <div>
                      <p className="text-gray-500 text-xs mb-1">Patriots</p>
                      <p className="text-white font-medium">127 hours since Tampa Bay</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs mb-1">Jets</p>
                      <p className="text-white font-medium">125 hours since Cleveland</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs mb-1">Expected Impact</p>
                      <p className="text-white font-medium">6-8% Q4 performance decline</p>
                    </div>
                    <div className="pt-3 border-t border-white/5">
                      <p className="text-gray-400 text-xs leading-relaxed">
                        Both teams on short rest, but Patriots' superior depth and conditioning mitigate fatigue effects.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Tactical Breakdown */}
            <section className="mb-10 sm:mb-12 md:mb-16">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <Map className="h-4 w-4 sm:h-5 sm:w-5 text-[#00E5FF]" />
                <h2 className="text-lg sm:text-xl md:text-2xl font-heading font-bold uppercase tracking-wider">Tactical Breakdown</h2>
              </div>

              <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
                <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-black/40 p-4 sm:p-5 md:p-6">
                  <h3 className="text-base sm:text-lg font-heading font-bold text-white mb-3 sm:mb-4">Patriots Offense vs Jets Defense</h3>
                  <div className="space-y-3 sm:space-y-4 text-xs sm:text-sm text-gray-400">
                    <div>
                      <p className="text-white font-medium mb-2">Fourth-Down Aggression</p>
                      <p className="leading-relaxed">
                        Patriots ranked 3rd in fourth-down conversion (80%); 85.7% at home. Expect 2-4 attempts with Jets' depleted front seven unable to generate short-yardage stops.
                      </p>
                    </div>
                    <div>
                      <p className="text-white font-medium mb-2">Play-Action Dominance</p>
                      <p className="leading-relaxed">
                        Maye's 122.7 deep-ball passer rating (2nd in NFL) versus Jets' 25th-ranked secondary. Patriots deploy 12-personnel to create favorable box counts, then attack vacated zones.
                      </p>
                    </div>
                    <div>
                      <p className="text-white font-medium mb-2">OL vs DL Mismatch</p>
                      <p className="leading-relaxed">
                        Without Quinnen Williams, Jets' interior rush evaporated. Javon Kinlaw and Haason Reddick combined for only 6 sacks. Patriots control line of scrimmage.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-black/40 p-4 sm:p-5 md:p-6">
                  <h3 className="text-base sm:text-lg font-heading font-bold text-white mb-3 sm:mb-4">Jets Offense vs Patriots Defense</h3>
                  <div className="space-y-3 sm:space-y-4 text-xs sm:text-sm text-gray-400">
                    <div>
                      <p className="text-white font-medium mb-2">One-Dimensional Attack</p>
                      <p className="leading-relaxed">
                        32nd-ranked passing attack (169.6 yards/game) forces predictable run schemes. Patriots deploy 8-man boxes on 67% of first/second downs, stuffing Hall.
                      </p>
                    </div>
                    <div>
                      <p className="text-white font-medium mb-2">Fields Under Siege</p>
                      <p className="leading-relaxed">
                        Patriots' pass rush (24 sacks, 7 INTs) versus Jets' OL allowing 27 sacks through 9 games. Blitz packages from Landry (5.5 sacks) and Chaisson (6.5 sacks) collapse pocket.
                      </p>
                    </div>
                    <div>
                      <p className="text-white font-medium mb-2">Secondary Advantage</p>
                      <p className="leading-relaxed">
                        Christian Gonzalez shadows Jets' WR1 replacement Metchie. Without Wilson and Gardner, Jets lack both offensive firepower and defensive coverage.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 sm:mt-6 rounded-xl sm:rounded-2xl border border-[#00E5FF]/20 bg-[#00E5FF]/5 p-4 sm:p-5 md:p-6">
                <h3 className="text-base sm:text-lg font-heading font-bold text-[#00E5FF] mb-3">Coaching Edge</h3>
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 text-xs sm:text-sm">
                  <div>
                    <p className="text-white font-medium mb-2">Mike Vrabel (Patriots)</p>
                    <p className="text-gray-400 leading-relaxed">
                      First-year HC orchestrating remarkable turnaround (8-2 from consecutive 4-13 seasons). Known for aggressive fourth-down calls and masterful halftime adjustments. Patriots scored 10 points in Q3 after 14-7 halftime lead.
                    </p>
                  </div>
                  <div>
                    <p className="text-white font-medium mb-2">Aaron Glenn (Jets)</p>
                    <p className="text-gray-400 leading-relaxed">
                      Rookie HC struggling at 2-7 with poor in-game adjustments. Burned all timeouts before critical Q4 drive, eliminating two-minute offense. Pattern repeated from Weeks 6, 8, 10.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Historical Context */}
            <section className="mb-10 sm:mb-12 md:mb-16">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-[#00E5FF]" />
                <h2 className="text-lg sm:text-xl md:text-2xl font-heading font-bold uppercase tracking-wider">Historical Context</h2>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-black/40 p-4 sm:p-5 md:p-6">
                  <h3 className="text-base sm:text-lg font-heading font-bold text-white mb-3 sm:mb-4">Head-to-Head</h3>
                  <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Last 10 meetings</span>
                      <span className="text-white font-medium">Patriots 7-3</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Avg margin</span>
                      <span className="text-white font-medium">+12.3 points</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">At Gillette (last 6)</span>
                      <span className="text-white font-medium">5-1 (+16.4)</span>
                    </div>
                    <div className="pt-3 border-t border-white/5">
                      <p className="text-gray-400 text-xs leading-relaxed">
                        Jets held under 17 points in 8 of last 10 games vs Patriots. Consistent offensive futility.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-black/40 p-4 sm:p-5 md:p-6">
                  <h3 className="text-base sm:text-lg font-heading font-bold text-white mb-3 sm:mb-4">Patriots Efficiency</h3>
                  <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Success rate</span>
                      <span className="text-white font-medium">44.76% (13th)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Explosive play rate</span>
                      <span className="text-white font-medium">12.8% (10th)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">3rd down conversion</span>
                      <span className="text-white font-medium">42.0% (10th)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Red zone TD rate</span>
                      <span className="text-white font-medium">62% (8th)</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-black/40 p-4 sm:p-5 md:p-6">
                  <h3 className="text-base sm:text-lg font-heading font-bold text-white mb-3 sm:mb-4">Jets Struggles</h3>
                  <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Success rate</span>
                      <span className="text-white font-medium">40.79% (25th)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Explosive play rate</span>
                      <span className="text-white font-medium">6.2% (31st)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">3rd down conversion</span>
                      <span className="text-white font-medium">34.2% (28th)</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Sack rate allowed</span>
                      <span className="text-white font-medium">11.2% (4th-worst)</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Scenario Map */}
            <section className="mb-10 sm:mb-12 md:mb-16">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <Map className="h-4 w-4 sm:h-5 sm:w-5 text-[#00E5FF]" />
                <h2 className="text-lg sm:text-xl md:text-2xl font-heading font-bold uppercase tracking-wider">Game Flow Scenarios</h2>
              </div>

              <div className="space-y-3 sm:space-y-4">
                <div className="rounded-xl sm:rounded-2xl border border-[#00E5FF]/30 bg-[#00E5FF]/5 p-4 sm:p-5 md:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-3 sm:mb-4">
                    <h3 className="text-base sm:text-lg md:text-xl font-heading font-bold text-white">Scenario 1: Methodical Mauling</h3>
                    <Badge className="bg-[#00E5FF]/20 text-[#00E5FF] border-[#00E5FF]/40 text-xs w-fit">62% Probability</Badge>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-400 mb-3 sm:mb-4 leading-relaxed">
                    Patriots establish run game early through Henderson, Maye avoids turnovers completing 72-78% of passes, Jets forced into obvious passing situations by Q2. Weather conditions favor Patriots' physical style.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 md:gap-4 text-[10px] sm:text-xs">
                    <div className="rounded-lg bg-black/40 p-3">
                      <p className="text-gray-500 mb-1">Final Score</p>
                      <p className="text-white font-medium">Patriots 28-13</p>
                    </div>
                    <div className="rounded-lg bg-black/40 p-3">
                      <p className="text-gray-500 mb-1">Time of Possession</p>
                      <p className="text-white font-medium">Patriots 35:00</p>
                    </div>
                    <div className="rounded-lg bg-black/40 p-3">
                      <p className="text-gray-500 mb-1">Turnover Diff</p>
                      <p className="text-white font-medium">Patriots +2</p>
                    </div>
                    <div className="rounded-lg bg-black/40 p-3">
                      <p className="text-gray-500 mb-1">Henderson TDs</p>
                      <p className="text-white font-medium">2 total</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5 md:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-3 sm:mb-4">
                    <h3 className="text-base sm:text-lg md:text-xl font-heading font-bold text-white">Scenario 2: Low-Scoring Slog</h3>
                    <Badge variant="outline" className="border-white/20 text-gray-300 text-xs w-fit">25% Probability</Badge>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-400 mb-3 sm:mb-4 leading-relaxed">
                    Weather conditions degrade passing efficiency for both teams, multiple turnovers on wet ball, both offenses struggle in red zone settling for field goals. Special teams score potentially decisive.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 md:gap-4 text-[10px] sm:text-xs">
                    <div className="rounded-lg bg-black/40 p-3">
                      <p className="text-gray-500 mb-1">Final Score</p>
                      <p className="text-white font-medium">Patriots 20-10</p>
                    </div>
                    <div className="rounded-lg bg-black/40 p-3">
                      <p className="text-gray-500 mb-1">Combined Points</p>
                      <p className="text-white font-medium">Under 30</p>
                    </div>
                    <div className="rounded-lg bg-black/40 p-3">
                      <p className="text-gray-500 mb-1">Total Turnovers</p>
                      <p className="text-white font-medium">4-5 fumbles</p>
                    </div>
                    <div className="rounded-lg bg-black/40 p-3">
                      <p className="text-gray-500 mb-1">Field Goals</p>
                      <p className="text-white font-medium">4-5 combined</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5 md:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-3 sm:mb-4">
                    <h3 className="text-base sm:text-lg md:text-xl font-heading font-bold text-white">Scenario 3: Jets Upset Special</h3>
                    <Badge variant="outline" className="border-red-500/20 text-red-400 text-xs w-fit">10% Probability</Badge>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-400 mb-3 sm:mb-4 leading-relaxed">
                    Fields' rushing ability creates explosive plays (67+ yards, 1-2 rush TDs), Breece Hall breakout performance (120+ yards), Patriots' short-rest fatigue manifests in Q3/Q4, key Patriots turnover swings momentum.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 md:gap-4 text-[10px] sm:text-xs">
                    <div className="rounded-lg bg-black/40 p-3">
                      <p className="text-gray-500 mb-1">Final Score</p>
                      <p className="text-white font-medium">Jets 24-21</p>
                    </div>
                    <div className="rounded-lg bg-black/40 p-3">
                      <p className="text-gray-500 mb-1">Fields Rushing</p>
                      <p className="text-white font-medium">70-90 yards, 1-2 TDs</p>
                    </div>
                    <div className="rounded-lg bg-black/40 p-3">
                      <p className="text-gray-500 mb-1">Turnover Diff</p>
                      <p className="text-white font-medium">Jets +2-3</p>
                    </div>
                    <div className="rounded-lg bg-black/40 p-3">
                      <p className="text-gray-500 mb-1">Hall Yards</p>
                      <p className="text-white font-medium">120+ rushing</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Validated Results */}
            <section className="mb-6 sm:mb-8">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-400" />
                <h2 className="text-lg sm:text-xl md:text-2xl font-heading font-bold uppercase tracking-wider text-emerald-400">
                  Post-Game Validation
                </h2>
              </div>

              <div className="rounded-xl sm:rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
                <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <CheckCircle2 className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-400 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-heading font-bold text-white">Final Score: Patriots 27, Jets 14</h3>
                    <p className="text-emerald-300 text-xs sm:text-sm">Scenario 1 "Methodical Mauling" (62% probability) validated</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-4 sm:mt-6">
                  <div className="rounded-lg bg-black/40 p-3 sm:p-4 border border-emerald-500/20">
                    <p className="text-[10px] sm:text-xs text-gray-400 mb-1">Predicted Margin</p>
                    <p className="text-base sm:text-lg md:text-xl font-heading font-bold text-white">10-14 points</p>
                    <p className="text-[10px] sm:text-xs text-emerald-400 mt-1">✓ Actual: 13 points</p>
                  </div>
                  <div className="rounded-lg bg-black/40 p-3 sm:p-4 border border-emerald-500/20">
                    <p className="text-[10px] sm:text-xs text-gray-400 mb-1">Predicted Total</p>
                    <p className="text-base sm:text-lg md:text-xl font-heading font-bold text-white">38-42 points</p>
                    <p className="text-[10px] sm:text-xs text-emerald-400 mt-1">✓ Actual: 41 points</p>
                  </div>
                  <div className="rounded-lg bg-black/40 p-3 sm:p-4 border border-emerald-500/20">
                    <p className="text-[10px] sm:text-xs text-gray-400 mb-1">Game Style</p>
                    <p className="text-base sm:text-lg md:text-xl font-heading font-bold text-white">Run-Heavy</p>
                    <p className="text-[10px] sm:text-xs text-emerald-400 mt-1">✓ 336 total yards</p>
                  </div>
                  <div className="rounded-lg bg-black/40 p-3 sm:p-4 border border-emerald-500/20">
                    <p className="text-[10px] sm:text-xs text-gray-400 mb-1">Winner</p>
                    <p className="text-base sm:text-lg md:text-xl font-heading font-bold text-white">Patriots</p>
                    <p className="text-[10px] sm:text-xs text-emerald-400 mt-1">✓ High confidence</p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                {[
                  {
                    prediction: 'TreVeyon Henderson Multi-TD Performance',
                    confidence: '92%',
                    projected: '18-22 touches, 85-105 total yards, 2-3 TDs',
                    actual: '19 carries, 62 rush yards; 5 receptions, 31 rec yards; 3 total TDs (2 rush, 1 receiving)',
                    analysis: 'Volume and touchdown projection hit perfectly. Henderson dominated red-zone touches as predicted with Stevenson out.'
                  },
                  {
                    prediction: 'Justin Fields Under 150 Passing Yards',
                    confidence: '85%',
                    projected: '90-140 passing yards, 58-65% completion',
                    actual: '15-of-26 (57.7%), 116 passing yards, 1 TD',
                    analysis: 'Without Wilson, Fields struggled exactly as forecasted. Patriots defense held him well below 150-yard threshold.'
                  },
                  {
                    prediction: 'Stefon Diggs 90+ Receiving Yards',
                    confidence: '78%',
                    projected: '7-10 catches, 95-115 yards, 0-1 TD',
                    actual: '9 receptions, 105 yards, 0 TDs',
                    analysis: 'Nearly perfect projection. Without Gardner, Diggs exploited Jets secondary for 105 yards on 9 catches.'
                  },
                  {
                    prediction: 'Drake Maye 270+ Passing Yards',
                    confidence: '82%',
                    projected: '270-300 yards, 2-3 total TDs, 0-1 INT',
                    actual: '25-of-34 (73.5%), 281 yards, 1 pass TD, 0 INT',
                    analysis: 'Maye exceeded passing projection with zero turnovers. Controlled game efficiently without needing multiple TDs.'
                  },
                  {
                    prediction: 'Patriots 4th Down Conversions',
                    confidence: '80%',
                    projected: '2-4 attempts, 80%+ conversion rate',
                    actual: '2-of-3 conversions, including crucial TD before halftime',
                    analysis: 'Vrabel\'s aggressive fourth-down philosophy validated. Controversial "kneel" before halftime TD exemplified calculated risk-taking.'
                  },
                  {
                    prediction: 'Weather Impact on Game Style',
                    confidence: '75%',
                    projected: 'Ball-control offense, reduced deep passing, run-heavy',
                    actual: '39°F conditions, Patriots 336 total yards, methodical drives',
                    analysis: 'Cold, windy conditions favored Patriots physical style exactly as forecasted. Ball security premium validated.'
                  }
                ].map((result) => (
                  <div key={result.prediction} className="rounded-xl sm:rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/10 to-transparent p-4 sm:p-5 md:p-6">
                    <div className="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4">
                      <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-400 flex-shrink-0 mt-1" />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-heading font-bold text-white mb-1">
                          {result.prediction}
                        </h3>
                        <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-xs">
                          {result.confidence} confidence
                        </Badge>
                      </div>
                    </div>
                    <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                      <div className="rounded-lg bg-black/40 p-2 sm:p-3 border border-white/5">
                        <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Pre-Game Projection</p>
                        <p className="text-gray-300 text-xs sm:text-sm">{result.projected}</p>
                      </div>
                      <div className="rounded-lg bg-emerald-500/5 p-2 sm:p-3 border border-emerald-500/20">
                        <p className="text-[10px] sm:text-xs text-emerald-400 mb-1">Actual Result</p>
                        <p className="text-white font-medium text-xs sm:text-sm">{result.actual}</p>
                      </div>
                      <p className="text-gray-400 leading-relaxed text-[10px] sm:text-xs">
                        {result.analysis}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Footer CTA */}
            <div className="rounded-xl sm:rounded-2xl border border-[#00E5FF]/30 bg-gradient-to-br from-[#00E5FF]/10 to-transparent p-6 sm:p-8 text-center">
              <h3 className="text-xl sm:text-2xl font-heading font-bold text-white mb-2 sm:mb-3">
                This is the intelligence edge you deserve
              </h3>
              <p className="text-sm sm:text-base text-gray-400 mb-4 sm:mb-6 max-w-2xl mx-auto">
                Pre-order early access and receive comprehensive intelligence reports like this for every game across major sports leagues. Delivered hours before kickoff with exclusive insights no one else has.
              </p>
              <Badge className="bg-[#00E5FF]/20 text-[#00E5FF] border-[#00E5FF]/40 text-xs">
                Limited Early Access Spots Available
              </Badge>
            </div>
            </>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}


